import axios from "axios";
import router from "@/router";

const state = () => ({
  devices: [],
  course_devices: [],
  bulkUploadData: {},
  recordsFiltered: 0,
  recordsTotal: 0,
  device_query: {
    draw: 0,
    start: 0,
    length: 10,
    search: "",
    orderBy: "id",
    dir: "asc",
  },
  current_page: 1,
  last_page: 1,
  per_page: 5,
  total: 0,
  errors: [],
  loading: false,
  message: "",
  logs: "",
  actions: {
    logs: { action: "send-logs", limit: 5000 },
    config: { action: "get-config" },
    courses: { action: "get-courses" },
    advertisments: { actions: "get-advertisements" },
    register: { action: "re-register", unit: 1, type: 1 },
  },
});

const mutations = {
  SET_DEVICES(state, devices) {
    state.devices = devices;
  },
  SET_COURSE_DEVICES(state, devices) {
    state.course_devices = devices;
  },
  SET_DEVICES_PARAMS(state, params) {
    state.recordsFiltered = params.recordsFiltered;
    state.recordsTotal = params.recordsTotal;
  },
  SET_RECORDS(state, records) {
    state.recordsFiltered = records.filtered;
    state.recordsTotal = records.total;
  },
  SET_DEVICE_QUERY(state, query) {
    state.device_query = query;
  },
  RESET_DEVICE_QUERY(state) {
    state.device_query = {
      draw: 0,
      start: 0,
      length: 10,
      search: "",
      orderBy: "id",
      dir: "asc",
      filterApplied: false,
    };
    state.recordsFiltered = 0;
    state.recordsTotal = 0;
    state.devices = [];
  },
  SET_BULK_UPLOAD_DATA(state, data) {
    state.bulkUploadData = data;
  },
  SET_CURRENT_PAGE(state, page) {
    state.current_page = page;
  },
  SET_LAST_PAGE(state, page) {
    state.last_page = page;
  },
  SET_PER_PAGE(state, per_page) {
    state.per_page = per_page;
  },
  SET_TOTAL(state, total) {
    state.total = total;
  },
  SET_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_LOADING(state) {
    state.loading = true;
  },
  CLEAR_LOADING(state) {
    state.loading = false;
  },
  SET_DEVICE_LOGS(state, logs) {
    state.logs = logs;
  },
};

const getters = {
  getDevices: (state) => {
    return state.devices;
  },
  getCourseDevices: (state) => {
    return state.course_devices;
  },
  getRecordsFiltered: (state) => {
    return state.recordsFiltered;
  },
  getRecordsTotal: (state) => {
    return state.recordsTotal;
  },
  getDeviceQuery: (state) => {
    return state.device_query;
  },
  getDevicesParams: (state) => {
    return state.devicesParams;
  },
  getbulkUploadData: (state) => {
    return state.bulkUploadData;
  },
  getCurrentPage: (state) => {
    return state.current_page;
  },
  getPerPage: (state) => {
    return state.per_page;
  },
  getTotal: (state) => {
    return state.total;
  },
  isLoading: (state) => {
    return state.loading;
  },
};

const actions = {
  async fetchCourseDevices({ commit }, payload) {
    commit("SET_LOADING");
    // /api/tm/courses/{course}/devices?page=1&per_page=3&sort_by=id&sort_order=desc&search=ste
    const course = payload.courseId;
    const url =
      process.env.VUE_APP_API_URL +
      "/tm/courses/" +
      course +
      "/devices" +
      queryBuilder(payload);

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      commit("SET_COURSE_DEVICES", response.data.data);
      commit("SET_CURRENT_PAGE", response.data.current_page);
      commit("SET_LAST_PAGE", response.data.last_page);
      commit("SET_PER_PAGE", response.data.per_page);
      commit("SET_TOTAL", response.data.total);
      commit("SET_ERRORS", []);
      commit("CLEAR_LOADING");
    } catch (error) {
      console.log(error);
      commit("CLEAR_LOADING");
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Fetch Devices failed"
      );
    }
  },
  async fetchDevices({ commit, state, dispatch }) {
    commit("SET_LOADING");

    // Only return empty results if search is empty AND no filters are applied AND no explicit filter action was taken
    if (
      state.device_query.search === "" &&
      !state.device_query.courses &&
      !state.device_query.device_types &&
      !state.device_query.filterApplied
    ) {
      console.log("Empty search with no filters - returning empty results");
      commit("SET_DEVICES", []);
      commit("SET_DEVICES_PARAMS", {
        recordsFiltered: 0,
        recordsTotal: 0,
      });
      commit("CLEAR_LOADING");
      return { data: [] };
    }

    const url = process.env.VUE_APP_API_URL + "/devices/filter-devices";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, state.device_query, config);
      let devicesData = [];
      let recordsTotal = 0;
      let recordsFiltered = 0;

      if (Array.isArray(response.data)) {
        devicesData = response.data;
        recordsTotal = devicesData.length;
        recordsFiltered = devicesData.length;
      } else if (response.data.data) {
        if (Array.isArray(response.data.data)) {
          devicesData = response.data.data;
        } else if (typeof response.data.data === "object") {
          devicesData = [response.data.data];
        }
        recordsTotal = response.data.recordsTotal || devicesData.length;
        recordsFiltered = response.data.recordsFiltered || devicesData.length;
      } else if (response.data.device) {
        devicesData = [response.data.device];
        recordsTotal = 1;
        recordsFiltered = 1;
      }

      commit("SET_DEVICES", devicesData);
      commit("SET_DEVICES_PARAMS", {
        recordsFiltered: recordsFiltered,
        recordsTotal: recordsTotal,
      });

      commit("CLEAR_LOADING");
      return response.data;
    } catch (error) {
      commit("CLEAR_LOADING");
      console.error(error);
      commit("SET_DEVICES", []);
      dispatch(
        "toastNotification/displayToast",
        {
          message: "Fetch Devices problematic",
          type: "warning",
        },
        { root: true }
      );
    }
  },
  async fetchArchivedDevices({ commit, state }) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/devices/filter-deleted-devices";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, state.device_query, config);
      commit("SET_DEVICES", response.data.data);
      commit("SET_DEVICES_PARAMS", response.data);
      commit("CLEAR_LOADING");
      return response.data;
    } catch (error) {
      commit("CLEAR_LOADING");
      console.error(error);
      throw error?.response?.data;
    }
  },
  async exportDevicesCSV({ commit, state }) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/devices/export";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, state.device_query, config);
      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "Central_devices_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      commit("CLEAR_LOADING");
      return response.data;
    } catch (error) {
      commit("CLEAR_LOADING");
      console.log(error);
      throw error?.response?.data;
    }
  },
  setDeviceQuery({ commit }, payload) {
    commit("SET_DEVICE_QUERY", payload);
  },
  async fetchDeviceLogs({ commit }, payload) {
    commit("SET_LOADING");
    const url =
      process.env.VUE_APP_API_URL + "/tm/app/viewlogs/" + payload.imei;

    window.open(url, "_blank");

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      console.log("debug-fetchDeviceLogs", response.data);
      commit("SET_DEVICE_LOGS", response.data);
      commit("SET_ERRORS", []);
      commit("CLEAR_LOADING");
    } catch (error) {
      console.log(error);
      commit("CLEAR_LOADING");
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Fetch Device Logs failed"
      );
    }
  },
  clearbulkUploadData({ commit }) {
    commit("SET_BULK_UPLOAD_DATA", {});
  },
  async uploadBulkDevices({ commit }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/bulk-devices-upload";

    const formData = new FormData();
    formData.append("uploaded_file", payload); // Changed "file" to "uploaded_file" to match original request

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
        "Content-Type": "multipart/form-data",
      },
    };

    try {
      const response = await axios.post(url, formData, config); // Corrected parameter order

      if (response?.data?.status === false) {
        throw response.data;
      } else {
        commit("SET_BULK_UPLOAD_DATA", response.data);
        router.push({
          name: "bulk-devices-upload",
          params: response.data,
        });
      }
      return response.data;
    } catch (error) {
      commit("SET_DEVICES", {});
      if (error?.msg) {
        throw error;
      } else {
        throw error?.response?.data;
      }
    }
  },
  async addDevice({ dispatch }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/add-device";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, payload, config);

      if (response.data.duplicate) {
        throw response.data;
      }

      dispatch("fetchDevices");
    } catch (error) {
      if (error?.msg) {
        throw error;
      } else {
        throw error?.response?.data;
      }
    }
  },
  async sendCommands(_, payload) {
    const url = process.env.VUE_APP_API_URL + "/centralip/sendcommands";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      await axios.post(url, payload, config);
    } catch (error) {
      throw error.response?.data;
    }
  },
  async multiDeviceSearch({ commit }, payload) {
    const url =
      process.env.VUE_APP_API_URL + "/devices/multiple-devices-search";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, payload, config);

      if (response.data.data.length === 0) {
        throw { message: "No matching devices found." };
      }
      commit("SET_DEVICES", response.data?.data ? response.data.data : []);
    } catch (error) {
      if (error?.message) {
        throw error;
      } else {
        throw error.response?.data;
      }
    }
  },
  async clearMultiDeviceCache({ commit }, payload) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/devices/clear-devices-cache";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.post(url, payload, config);

      if (response.data && response.data.status === false) {
        throw response.data;
      }

      commit("SET_ERRORS", []);
      commit("CLEAR_LOADING");
      return response.data;
    } catch (error) {
      commit("CLEAR_LOADING");
      if (error.response && error.response.data) {
        throw error.response.data;
      } else if (error?.message && !error.response) {
        throw error;
      } else {
        throw { message: "An unexpected error occurred." };
      }
    }
  },
  async moveDevices({ commit, dispatch }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/move-devices";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.patch(url, payload, config);
      if (response.status === false) {
        throw response.data;
      }

      await dispatch("fetchDevices");
      if (response?.data?.errors?.length > 0) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Move devices",
            message: `${response.data.errors.join("\n ")}`,
            type: "warning",
          },
          { root: true }
        );
      }
      return response.data;
    } catch (error) {
      throw error?.response?.data;
    }
  },
  async updateDevice({ dispatch }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/update-device";
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    const modifiedPayload = { ...payload };

    if ("device_class_id" in modifiedPayload) {
      modifiedPayload.device_class = modifiedPayload.device_class_id;
      delete modifiedPayload.device_class_id;
    }

    if ("device_type_id" in modifiedPayload) {
      modifiedPayload.device_type = modifiedPayload.device_type_id;
      delete modifiedPayload.device_type_id;
    }

    try {
      const response = await axios.patch(url, modifiedPayload, config);
      if (response.status === false) {
        throw response.data;
      }
      dispatch("fetchDevices");
    } catch (error) {
      throw error?.response?.data;
    }
  },
  async archiveDevices({ commit, dispatch }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/delete-device";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
      data: payload,
    };

    try {
      const response = await axios.delete(url, config);
      dispatch("fetchDevices");
      if (response?.data?.errors?.length > 0) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Archive devices",
            message: `${response.data.errors.join("\n ")}`,
            type: "warning",
          },
          { root: true }
        );
      }
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async permenantluDeleteDevices({ dispatch }, payload) {
    const url =
      process.env.VUE_APP_API_URL + "/devices/permanently-delete-devices";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
      data: payload,
    };

    try {
      const response = await axios.delete(url, config);
      dispatch("fetchArchivedDevices");
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  },
  async unassignDevices({ commit, dispatch }, payload) {
    const url = process.env.VUE_APP_API_URL + "/devices/unassign-devices";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
      data: payload,
    };

    try {
      const response = await axios.delete(url, config);
      dispatch("fetchDevices");
      if (response?.data?.errors?.length > 0) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Unassign devices",
            message: `${response.data.errors.join("\n ")}`,
            type: "warning",
          },
          { root: true }
        );
      }
    } catch (error) {
      console.error(error);
      throw error.response.data;
    }
  },
  async deleteDevices({ commit }, payload) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/devices/delete-device";

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      await axios.post(url, payload, config);
      commit("SET_ERRORS", []);
      commit("CLEAR_LOADING");
    } catch (error) {
      commit("CLEAR_LOADING");
      console.log(error);
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Delete device failed"
      );
    }
  },
  async sendCommand({ commit, state, dispatch }, payload) {
    commit("SET_LOADING");
    const courseId = payload.courseId;
    const url =
      process.env.VUE_APP_API_URL + `/courses/${courseId}/send-command`;

    const actionType = payload.actionType;
    const action = state.actions[actionType];

    const reqPayload = {
      ids: payload.ids,
      ...action,
    };

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    let message;
    try {
      const response = await axios.post(url, reqPayload, config);
      message = "Command sent successfully";
      dispatch(
        "toastNotification/displayToast",
        { message, type: "success" },
        { root: true }
      );
      commit("SET_ERRORS", []);
      commit("CLEAR_LOADING");
      return response.data;
    } catch (error) {
      message = error.response?.data?.message || "Failed to send command";
      dispatch(
        "toastNotification/displayToast",
        { message, type: "error" },
        { root: true }
      );
      commit("CLEAR_LOADING");
      console.log(error);
      commit(
        "SET_ERRORS",
        error.response?.data?.message || `${reqPayload.action} action failed`
      );
    }
  },
  async restoreDevices({ commit, dispatch }, payload) {
    commit("SET_LOADING");
    const url = process.env.VUE_APP_API_URL + "/devices/restore";
    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };
    try {
      const response = await axios.post(url, payload, config);
      dispatch(
        "toastNotification/displayToast",
        {
          message: response?.data?.msg || "Devices restored successfully",
          type: "success",
        },
        { root: true }
      );
      dispatch("fetchArchivedDevices");
    } catch (error) {
      dispatch(
        "toastNotification/displayToast",
        {
          message: error.response?.data?.message || "Failed to restore devices",
          type: "error",
        },
        { root: true }
      );
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Failed to restore devices"
      );
    } finally {
      commit("CLEAR_LOADING");
    }
  },
};

const queryBuilder = (payload) => {
  let query = [];
  if (payload.page) {
    query.push("page=" + payload.page);
  }

  if (payload.pageSize) {
    query.push("per_page=" + payload.pageSize);
  }

  if (payload.searchTerm) {
    query.push("search=" + payload.searchTerm);
  }

  if (payload.sortOrder) {
    query.push("sort_by=" + payload.sortOrder.key);
    query.push("sort_order=" + payload.sortOrder.direction);
  }

  if (query.length > 0) {
    query = "?" + query.join("&");
    return query;
  }

  return "";
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
